import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmailList(values = []) {
  const list = Array.isArray(values)
    ? values
    : String(values || "").split(/[;,\n]+/);
  const seen = new Set();

  return list
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((email) => {
      if (!email || !EMAIL_RE.test(email) || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

function getManagerEmails(record = {}) {
  return normalizeEmailList([
    ...(Array.isArray(record.managerEmails) ? record.managerEmails : []),
    record.managerEmail || "",
  ]);
}

function getCompanyIds(driver = {}) {
  const ids = Array.isArray(driver.companyIds) ? driver.companyIds : [];
  return driver.companyId && !ids.includes(driver.companyId)
    ? [...ids, driver.companyId]
    : ids;
}

function driverLabel(driver = {}) {
  return driver.name || driver.displayName || driver.email || driver.id || "Driver";
}

export default function AdminManagerAssignments() {
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [managerDirectory, setManagerDirectory] = useState([]);
  const [managerInput, setManagerInput] = useState("");
  const [selectedManager, setSelectedManager] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    return onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data(), name: d.data()?.name || d.id }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setCompanies(list);
    });
  }, []);

  useEffect(() => {
    return onSnapshot(collection(db, "notificationManagers"), (snap) => {
      const list = snap.docs
        .map((d) => normalizeEmailList(d.data()?.email || d.id)[0])
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setManagerDirectory(list);
    });
  }, []);

  useEffect(() => {
    const qy = query(collection(db, "users"), where("role", "==", "driver"));
    return onSnapshot(qy, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => driverLabel(a).localeCompare(driverLabel(b)));
      setDrivers(list);
    });
  }, []);

  const companyById = useMemo(() => {
    const map = new Map();
    companies.forEach((company) => map.set(company.id, company));
    return map;
  }, [companies]);

  const managers = useMemo(() => {
    const emails = [
      ...managerDirectory,
      ...companies.flatMap(getManagerEmails),
      ...drivers.flatMap(getManagerEmails),
    ];
    return normalizeEmailList(emails).sort((a, b) => a.localeCompare(b));
  }, [companies, drivers, managerDirectory]);

  useEffect(() => {
    if (!selectedManager && managers.length) {
      setSelectedManager(managers[0]);
    }
  }, [managers, selectedManager]);

  const filteredCompanies = useMemo(() => {
    const q = companyFilter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((company) =>
      `${company.name || ""} ${getManagerEmails(company).join(" ")}`
        .toLowerCase()
        .includes(q),
    );
  }, [companies, companyFilter]);

  const filteredDrivers = useMemo(() => {
    const q = driverFilter.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((driver) => {
      const companyNames = getCompanyIds(driver)
        .map((id) => companyById.get(id)?.name || id)
        .join(" ");
      return `${driverLabel(driver)} ${driver.email || ""} ${companyNames} ${getManagerEmails(driver).join(" ")}`
        .toLowerCase()
        .includes(q);
    });
  }, [companyById, drivers, driverFilter]);

  async function addManager(event) {
    event.preventDefault();
    const [email] = normalizeEmailList(managerInput);
    if (!email || managerInput.split(/[;,\n]+/).filter(Boolean).length !== 1) {
      setMessage("Enter one valid manager email address.");
      return;
    }

    setSavingKey(`manager:${email}`);
    setMessage("");
    try {
      await setDoc(
        doc(db, "notificationManagers", email),
        {
          email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setSelectedManager(email);
      setManagerInput("");
      setMessage("Manager ready to assign.");
    } catch (error) {
      console.error("Failed to add manager email:", error);
      setMessage("Could not add this manager email. Please try again.");
    } finally {
      setSavingKey("");
    }
  }

  async function toggleManager({ target, id, currentEmails, checked }) {
    if (!selectedManager) return;
    const field = "managerEmails";
    const next = checked
      ? normalizeEmailList([...currentEmails, selectedManager])
      : normalizeEmailList(currentEmails.filter((email) => email !== selectedManager));
    const collectionName = target === "company" ? "companies" : "users";
    const key = `${target}:${id}`;

    setSavingKey(key);
    setMessage("");
    try {
      await updateDoc(doc(db, collectionName, id), {
        [field]: next,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed to update manager assignment:", error);
      setMessage("Could not save this manager assignment. Please try again.");
    } finally {
      setSavingKey("");
    }
  }

  const selectedCompanyCount = companies.filter((company) =>
    getManagerEmails(company).includes(selectedManager),
  ).length;
  const selectedDriverCount = drivers.filter((driver) =>
    getManagerEmails(driver).includes(selectedManager),
  ).length;

  return (
    <div className="w-full space-y-6 px-4 py-4 text-gray-900 sm:px-6 sm:py-6 xl:px-8">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 sm:px-6">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
              Admin
            </span>
            <h1 className="text-lg font-semibold tracking-tight text-gray-950">
              Manager assignments
            </h1>
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-semibold text-gray-900">{managers.length}</span> managers
            {" · "}
            <span className="font-semibold text-gray-900">{companies.length}</span> companies
            {" · "}
            <span className="font-semibold text-gray-900">{drivers.length}</span> drivers
            {" · "}
            <span className="font-semibold text-gray-900">{selectedCompanyCount + selectedDriverCount}</span> selected links
          </div>
        </div>

        <div className="grid gap-4 px-5 py-4 sm:px-6 lg:grid-cols-[minmax(260px,360px)_1fr]">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
              Manager
            </label>
            <select
              value={selectedManager}
              onChange={(e) => {
                setSelectedManager(e.target.value);
                setMessage("");
              }}
              className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
            >
              {!managers.length && <option value="">No managers yet</option>}
              {managers.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
              {selectedManager && !managers.includes(selectedManager) && (
                <option value={selectedManager}>{selectedManager}</option>
              )}
            </select>
          </div>

          <form onSubmit={addManager} className="lg:self-end">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
              Add manager email
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={managerInput}
                onChange={(e) => setManagerInput(e.target.value)}
                placeholder="manager@example.com"
                className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
              />
              <button
                type="submit"
                className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-black disabled:opacity-50"
                disabled={!managerInput.trim() || savingKey.startsWith("manager:")}
              >
                {savingKey.startsWith("manager:") ? "Adding..." : "Add"}
              </button>
            </div>
            {message && <div className="mt-2 text-sm text-gray-600">{message}</div>}
          </form>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <AssignmentPanel
          title="Companies"
          description="Company manager emails are used for timesheets, leave requests, and company notifications."
          filter={companyFilter}
          onFilterChange={setCompanyFilter}
          placeholder="Search companies or manager emails..."
        >
          {filteredCompanies.length === 0 ? (
            <EmptyState label="No companies found." />
          ) : (
            filteredCompanies.map((company) => {
              const emails = getManagerEmails(company);
              const checked = selectedManager && emails.includes(selectedManager);
              const key = `company:${company.id}`;

              return (
                <AssignmentRow
                  key={company.id}
                  title={company.name || company.id}
                  subtitle={`${emails.length} manager${emails.length === 1 ? "" : "s"}`}
                  emails={emails}
                  checked={checked}
                  disabled={!selectedManager || savingKey === key}
                  onChange={() =>
                    toggleManager({
                      target: "company",
                      id: company.id,
                      currentEmails: emails,
                      checked: !checked,
                    })
                  }
                />
              );
            })
          )}
        </AssignmentPanel>

        <AssignmentPanel
          title="Drivers"
          description="Direct driver managers are notified in addition to company managers."
          filter={driverFilter}
          onFilterChange={setDriverFilter}
          placeholder="Search drivers, companies, or manager emails..."
        >
          {filteredDrivers.length === 0 ? (
            <EmptyState label="No drivers found." />
          ) : (
            filteredDrivers.map((driver) => {
              const emails = getManagerEmails(driver);
              const checked = selectedManager && emails.includes(selectedManager);
              const key = `driver:${driver.id}`;
              const companyNames = getCompanyIds(driver)
                .map((id) => companyById.get(id)?.name || id)
                .join(", ");

              return (
                <AssignmentRow
                  key={driver.id}
                  title={driverLabel(driver)}
                  subtitle={companyNames || "No companies assigned"}
                  emails={emails}
                  checked={checked}
                  disabled={!selectedManager || savingKey === key}
                  onChange={() =>
                    toggleManager({
                      target: "driver",
                      id: driver.id,
                      currentEmails: emails,
                      checked: !checked,
                    })
                  }
                />
              );
            })
          )}
        </AssignmentPanel>
      </div>
    </div>
  );
}

function AssignmentPanel({ title, description, filter, onFilterChange, placeholder, children }) {
  return (
    <section className="rounded-[24px] border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
          <input
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100 md:w-72"
          />
        </div>
      </div>
      <div className="divide-y divide-gray-100">{children}</div>
    </section>
  );
}

function AssignmentRow({ title, subtitle, emails, checked, disabled, onChange }) {
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="mt-1 text-sm text-gray-500">{subtitle}</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {emails.length ? (
            emails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"
              >
                {email}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-500">No direct manager assigned</span>
          )}
        </div>
      </div>

      <label
        className={`inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
          checked
            ? "border-emerald-300 bg-emerald-50 text-emerald-900"
            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <input
          type="checkbox"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={onChange}
          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        />
        Assigned
      </label>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="px-4 py-8 text-center text-sm text-gray-500 sm:px-5">
      {label}
    </div>
  );
}
